import { FONT_OPTIONS } from '../../../services';
import { SharedBranding } from '../../../services';

// #region DEFAULT_BRANDING
// Mirrors the order-handler defaults. Used before the payload arrives and by
// the branding editor preview when nothing has been saved yet.
export const DEFAULT_BRANDING: SharedBranding = {
    logo_url: null,
    cover_image_url: null,
    primary_color: '#1F2937',
    accent_color: '#0F766E',
    font_key: 'inter',
    welcome_headline: 'Hi there, pick your favorites',
    welcome_body: 'Review each wall below and choose your favorite design. Your picks save automatically.',
    contact_name: null,
    contact_email: null,
    contact_phone: null,
    contact_website: null
};

// #region hexToRgb
// Tolerant parse — returns null for anything that isn't #RGB / #RRGGBB.
export const hexToRgb = (hex: string | null | undefined): { r: number; g: number; b: number } | null => {
    if (typeof hex !== 'string') return null;
    let value = hex.trim().replace('#', '');
    if (value.length === 3) value = value.split('').map((char) => char + char).join('');
    if (value.length !== 6) return null;

    const parsed = parseInt(value, 16);
    if (Number.isNaN(parsed)) return null;

    return {
        r: (parsed >> 16) & 255,
        g: (parsed >> 8) & 255,
        b: parsed & 255
    };
};

// #region relativeLuminance
// WCAG relative luminance, used to decide readable text over a brand color.
export const relativeLuminance = (hex: string): number => {
    const rgb = hexToRgb(hex);
    if (!rgb) return 0;
    const channel = (raw: number): number => {
        const value = raw / 255;
        return value <= 0.03928 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
    };
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
};

// #region contrastText
// Readable foreground for a filled brand-colored surface. A near-white primary
// gets dark text rather than rendering white-on-white.
export const contrastText = (hex: string): string => (relativeLuminance(hex) > 0.55 ? '#111827' : '#FFFFFF');

// #region withAlpha
// rgba() string for a hex color — used for soft tinted fills and rings.
export const withAlpha = (hex: string, alpha: number): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return `rgba(31, 41, 55, ${alpha})`;
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${alpha})`;
};

// #region lighten
// Mixes a color toward white by `amount` (0-1).
export const lighten = (hex: string, amount: number): string => {
    const rgb = hexToRgb(hex);
    if (!rgb) return hex;
    const mix = (channel: number): number => Math.round(channel + (255 - channel) * amount);
    const toHex = (channel: number): string => mix(channel).toString(16).padStart(2, '0');
    return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
};

// #region fontStackFor
// CSS stack for a font key, falling back to Inter/system when unknown.
export const fontStackFor = (fontKey: string | null | undefined): string => {
    const option = FONT_OPTIONS.find((candidate) => candidate.key === fontKey);
    return option ? option.stack : FONT_OPTIONS[0].stack;
};

// #region fontHrefFor
export const fontHrefFor = (fontKey: string | null | undefined): string => {
    const option = FONT_OPTIONS.find((candidate) => candidate.key === fontKey);
    return option ? option.href : FONT_OPTIONS[0].href;
};

// #region applyClientName
// Substitutes the {client_name} token in designer-authored copy (the server
// already does this for the live page; the preview needs it too).
export const applyClientName = (copy: string | null, clientName: string): string | null => {
    if (copy === null) return null;
    return copy.split('{client_name}').join(clientName);
};

// #region shareThemeVars
// The CSS custom properties every share surface reads. Applied inline so the
// same components theme correctly in both the live page and the editor preview.
export const shareThemeVars = (branding: SharedBranding): React.CSSProperties => {
    const primary = branding.primary_color || DEFAULT_BRANDING.primary_color;
    const accent = branding.accent_color || DEFAULT_BRANDING.accent_color;

    return {
        ['--share-primary' as any]: primary,
        ['--share-primary-contrast' as any]: contrastText(primary),
        ['--share-primary-soft' as any]: withAlpha(primary, 0.08),
        ['--share-accent' as any]: accent,
        ['--share-surface' as any]: '#FAF9F7',
        ['--share-surface-raised' as any]: '#FFFFFF',
        ['--share-border' as any]: '#E7E3DD',
        ['--share-text' as any]: '#1C1917',
        ['--share-text-muted' as any]: '#6B6560',
        ['--share-font' as any]: fontStackFor(branding.font_key)
    } as React.CSSProperties;
};
